import { Component, EventEmitter, HostListener, Input, OnInit, Output, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgTemplateOutlet } from '@angular/common';
import { ApiErrorResponse } from '../../../../shared/models/api-error-response.interface';
import { ApiCustomerSearchErrorCode, ApiCustomerSearchService, CustomerDetailsResponse } from '../../../../shared/services/api-customer-search.service';

type DetailsTab = 'customer' | 'email' | 'mobile' | 'response';

@Component({
  selector: 'app-customer-details-dialog',
  imports: [NgTemplateOutlet],
  templateUrl: './customer-details-dialog.component.html'
})
export class CustomerDetailsDialogComponent implements OnInit {
  // The parent renders this dialog with @if, so each open is a fresh instance that fetches once.
  @Input({ required: true }) surveyRequestId!: string;
  @Output() closed = new EventEmitter<void>();

  loading = signal(true);
  error = signal<string | null>(null);
  details = signal<CustomerDetailsResponse | null>(null);
  activeTab = signal<DetailsTab>('customer');

  constructor(private customerSearchService: ApiCustomerSearchService) {}

  ngOnInit(): void {
    this.customerSearchService.customerDetail(this.surveyRequestId).subscribe({
      next: (result) => {
        this.details.set(result);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(this.formatApiError(err, 'Failed to load customer details.'));
        this.loading.set(false);
      }
    });
  }

  @HostListener('document:keydown.escape')
  close(): void {
    this.closed.emit();
  }

  get customerName(): string {
    const data = this.details()?.customerData;
    return [data?.firstName, data?.lastName].filter(Boolean).join(' ');
  }

  // Key/value pairs of a flat record, in the API's own order; nested objects/arrays are listed
  // in `exclude` and rendered separately.
  fieldsOf(record: object | null | undefined, exclude: string[] = []): [string, unknown][] {
    if (!record) return [];
    return Object.entries(record).filter(([key]) => !exclude.includes(key));
  }

  formatFieldLabel(field: string): string {
    return field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());
  }

  formatValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  }

  private formatApiError(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 401) {
        return 'Unauthorized. Please sign in again.';
      }
      const apiError = err.error as ApiErrorResponse<ApiCustomerSearchErrorCode> | undefined;
      if (apiError?.code && apiError?.description) {
        const details = apiError.details?.length
          ? ` (${apiError.details.map(d => typeof d === 'string' ? d : JSON.stringify(d)).join('; ')})`
          : '';
        return `[${apiError.code}] ${apiError.description}${details}`;
      }
      return `${fallback} | ${err.message}`;
    }
    return `${fallback} | ${err}`;
  }
}
